import { Phone, ArrowDownRight, MapPin } from "lucide-react";
import { SITE, STOCK } from "@/lib/site";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="acasa"
      data-testid="hero-section"
      className="relative min-h-screen flex items-end overflow-hidden bg-[#fcf3e6]"
    >
      {/* Background palm leaves with light tint */}
      <div className="absolute inset-0">
        <img
          src={STOCK.heroLeaves}
          alt=""
          className="w-full h-full object-cover opacity-[0.18]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#fcf3e6]/40 via-[#fcf3e6]/60 to-[#fcf3e6]" />
      </div>

      {/* Soft tropical glow */}
      <div className="absolute -top-10 -right-10 w-80 h-80 rounded-full bg-[#048a93]/15 blur-3xl" />
      <div className="absolute bottom-1/3 -left-20 w-96 h-96 rounded-full bg-[#d4b895]/25 blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto w-full px-5 lg:px-10 pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-3 text-[#048a93] mb-7" data-testid="hero-eyebrow">
              <span className="h-px w-12 bg-[#048a93]" />
              <span className="uppercase text-[11px] tracking-[0.32em] font-sans-ui font-semibold">
                Locație de evenimente · Giulești, București
              </span>
            </div>

            <h1
              className="font-serif-display text-[#012a2d] font-light leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-[7.5rem]"
              data-testid="hero-title"
            >
              Ari<span className="italic text-[#048a93]">Danya</span>
              <span className="block text-2xl sm:text-3xl lg:text-4xl mt-4 tracking-[0.3em] uppercase font-sans-ui text-[#012a2d]/70">
                Garden · Pool & Lounge
              </span>
            </h1>

            <p
              className="font-serif-display italic text-[#012a2d]/85 text-2xl sm:text-3xl lg:text-4xl mt-10 max-w-2xl leading-snug"
              data-testid="hero-motto"
            >
              Relaxare. <span className="text-[#048a93] not-italic font-medium">Distracție.</span> Amintiri frumoase.
            </p>

            <div className="flex flex-wrap items-center gap-4 mt-12">
              <a
                href={SITE.phoneHref}
                className="group inline-flex items-center gap-3 bg-[#048a93] hover:bg-[#037078] text-white rounded-full pl-6 pr-3 py-3 transition-all hover:-translate-y-1 hover:shadow-2xl shadow-[#048a93]/30"
                data-testid="hero-call-button"
              >
                <Phone className="h-4 w-4" />
                <span className="font-sans-ui text-sm font-medium tracking-wide">Sună acum · {SITE.phoneDisplay}</span>
                <span className="h-9 w-9 rounded-full bg-[#fcf3e6] text-[#048a93] flex items-center justify-center group-hover:rotate-45 transition-transform">
                  <ArrowDownRight className="h-4 w-4" />
                </span>
              </a>
              <button
                onClick={() => scrollTo("contact")}
                className="inline-flex items-center gap-2 border border-[#012a2d]/25 hover:border-[#012a2d] text-[#012a2d] rounded-full px-7 py-3.5 transition-all font-sans-ui text-sm"
                data-testid="hero-reserve-button"
              >
                Rezervă data
              </button>
            </div>
          </div>

          <div className="lg:col-span-4 lg:pb-4">
            <div className="border-l border-[#012a2d]/15 pl-6 space-y-6">
              <div className="flex items-start gap-3 text-[#012a2d]/85">
                <MapPin className="h-5 w-5 text-[#048a93] mt-1 shrink-0" />
                <div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-[#048a93] font-semibold">Locație</div>
                  <div className="font-serif-display text-2xl mt-1 text-[#012a2d]">{SITE.location}</div>
                </div>
              </div>
              <div className="flex items-start gap-3 text-[#012a2d]/85">
                <Phone className="h-5 w-5 text-[#048a93] mt-1 shrink-0" />
                <div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-[#048a93] font-semibold">Rezervări</div>
                  <a href={SITE.phoneHref} className="font-serif-display text-2xl mt-1 block text-[#012a2d] hover:text-[#048a93] transition">
                    {SITE.phoneDisplay}
                  </a>
                </div>
              </div>
              <div className="text-[#012a2d]/70 text-sm leading-relaxed font-sans-ui">
                Piscină 10×5m, tobogan gonflabil, terasă, bar exterior, vatră de foc și grădină verde — totul într-un singur loc, pentru petreceri de poveste.
              </div>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="mt-16 lg:mt-24 overflow-hidden border-t border-b border-[#012a2d]/15 py-5">
          <div className="flex animate-marquee whitespace-nowrap gap-12 text-[#012a2d]/35 font-serif-display text-2xl italic">
            {Array.from({ length: 6 }).map((_, i) => (
              <span key={i} className="flex items-center gap-12">
                Relaxare
                <span className="text-[#048a93]">✦</span>
                Distracție
                <span className="text-[#048a93]">✦</span>
                Amintiri frumoase
                <span className="text-[#048a93]">✦</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
